import React, { useState, forwardRef, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Dialog,
  DialogContent,
  Stack,
  Chip,
  IconButton,
} from "@mui/material";
import Slider from "react-slick";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GlassPanel, ModernTextField } from "./styledComponents";

import BrazosMasculino from "../../assets/images/antropometria/brazos.png";
import BrazosFemenino from "../../assets/images/antropometria/brazosMujer.png";
import HombrosMasculino from "../../assets/images/antropometria/hombros.png";
import HombrosFemenino from "../../assets/images/antropometria/hombrosMujer.png";
import PectoralBustoMasculino from "../../assets/images/antropometria/pectoralBusto.png";
import PectoralBustoFemenino from "../../assets/images/antropometria/pectoralBustoMujer.png";
import AbdomenCinturaMasculino from "../../assets/images/antropometria/abdomenCintura.png";
import AbdomenCinturaFemenino from "../../assets/images/antropometria/abdomenCinturaMujer.png";
import CaderaGluteosMasculino from "../../assets/images/antropometria/caderaGluteos.png";
import CaderaGluteosFemenino from "../../assets/images/antropometria/caderaGluteosMujer.png";
import MusloMasculino from "../../assets/images/antropometria/muslo.png";
import MusloFemenino from "../../assets/images/antropometria/musloMujer.png";
import PantorrillaMasculino from "../../assets/images/antropometria/pantorrilla.png";
import PantorrillaFemenino from "../../assets/images/antropometria/pantorrillaMujer.png";

const CarouselArrow = ({ className, style, onClick, direction }) => (
  <IconButton
    className={className}
    onClick={onClick}
    style={style}
    sx={{
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      backgroundColor: "#fff",
      boxShadow: "0 10px 25px rgba(15,23,42,0.15)",
      "&:hover": { backgroundColor: "#f1f5f9" },
      ...(direction === "next" ? { right: -24 } : { left: -24 }),
    }}
  >
    {direction === "next" ? (
      <ArrowForwardIosIcon fontSize="small" />
    ) : (
      <ArrowBackIosNewIcon fontSize="small" />
    )}
  </IconButton>
);

const Antropometria = forwardRef(
  ({ formValues, formValuesBasic, onChange }, ref) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [currentImage, setCurrentImage] = useState(null);

    const handleInputChange = useCallback(
      (e) => {
        const { name, value } = e.target;
        onChange(name, value);
      },
      [onChange]
    );

    const handleImageClick = useCallback((image) => {
      setCurrentImage(image);
      setOpenDialog(true);
    }, []);

    const handleDialogClose = useCallback(() => {
      setOpenDialog(false);
    }, []);

    const today = useMemo(() => new Date().toISOString().split("T")[0], []);

    const carouselImages = useMemo(() => {
      const isFemale = formValuesBasic?.sexo === "femenino";
      return [
        {
          label: "Brazos",
          src: isFemale ? BrazosFemenino : BrazosMasculino,
        },
        {
          label: "Hombros",
          src: isFemale ? HombrosFemenino : HombrosMasculino,
        },
        {
          label: "Pectoral/Busto",
          src: isFemale ? PectoralBustoFemenino : PectoralBustoMasculino,
        },
        {
          label: "Abdomen/Cintura",
          src: isFemale ? AbdomenCinturaFemenino : AbdomenCinturaMasculino,
        },
        {
          label: "Cadera/Glúteos",
          src: isFemale ? CaderaGluteosFemenino : CaderaGluteosMasculino,
        },
        {
          label: "Muslo",
          src: isFemale ? MusloFemenino : MusloMasculino,
        },
        {
          label: "Pantorrilla",
          src: isFemale ? PantorrillaFemenino : PantorrillaMasculino,
        },
      ];
    }, [formValuesBasic?.sexo]);

    const carouselSettings = useMemo(
      () => ({
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 6,
        slidesToScroll: 1,
        nextArrow: <CarouselArrow direction="next" />,
        prevArrow: <CarouselArrow direction="prev" />,
        responsive: [
          { breakpoint: 1280, settings: { slidesToShow: 4 } },
          { breakpoint: 900, settings: { slidesToShow: 3 } },
          { breakpoint: 600, settings: { slidesToShow: 2 } },
        ],
      }),
      []
    );

    const inputFields = useMemo(
      () => [
        { label: "Brazos (cm)", name: "brazos" },
        { label: "Hombros (cm)", name: "hombros" },
        { label: "Pectoral/Busto (cm)", name: "pectoralBusto" },
        { label: "Abdomen/Cintura (cm)", name: "abdomenCintura" },
        { label: "Cadera/Glúteos (cm)", name: "caderaGluteos" },
        { label: "Muslo (cm)", name: "muslo" },
        { label: "Pantorrilla (cm)", name: "pantorrilla" },
      ],
      []
    );

    return (
      <Box ref={ref} sx={{ py: { xs: 2, md: 3 } }}>
        <Stack spacing={3}>
          <GlassPanel>
            <Stack spacing={2}>
              <Box>
                <Typography variant="overline" sx={{ letterSpacing: 3 }}>
                  Perímetros corporales
                </Typography>
                <Typography variant="h5" fontWeight={800} color="primary.main">
                  Antropometría guiada
                </Typography>
                <Typography color="text.secondary">
                  Usa las referencias visuales para tomar tus medidas con cinta
                  métrica flexible. Toca una imagen para ampliarla.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {["Auto medición", "Seguimiento"].map((tag) => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" />
                ))}
              </Stack>
              <Box sx={{ position: "relative", px: { xs: 3, sm: 4 } }}>
                <Slider {...carouselSettings}>
                  {carouselImages.map((image) => (
                    <Box key={image.label} sx={{ px: 1, textAlign: "center" }}>
                      <Box
                        onClick={() => handleImageClick(image.src)}
                        sx={{
                          position: "relative",
                          mx: "auto",
                          width: 110,
                          height: 110,
                          borderRadius: 4,
                          overflow: "hidden",
                          cursor: "pointer",
                          boxShadow: "0 15px 30px rgba(15,23,42,0.18)",
                        }}
                      >
                        <img
                          src={image.src}
                          alt={image.label}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          draggable="false"
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                              "linear-gradient(180deg, transparent, rgba(15,23,42,0.55))",
                            color: "#fff",
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, fontWeight: 600 }}
                      >
                        {image.label}
                      </Typography>
                    </Box>
                  ))}
                </Slider>
              </Box>
            </Stack>
          </GlassPanel>

          <GlassPanel>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <ModernTextField
                  label="Fecha"
                  name="fechaAntropometria"
                  value={formValues.fechaAntropometria || ""}
                  onChange={handleInputChange}
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: today }}
                  helperText="Ingresa la fecha en la que fueron tomadas estas medidas"
                  sx={{
                    "& input[type='date']::-webkit-calendar-picker-indicator": {
                      cursor: "pointer",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Apóyate de otra persona para asegurar lecturas precisas. Mide
                  el perímetro indicado y captura el dato en centímetros.
                </Typography>
              </Grid>
              {inputFields.map((field) => (
                <Grid item xs={12} md={6} key={field.name}>
                  <ModernTextField
                    label={field.label}
                    name={field.name}
                    value={formValues[field.name] || ""}
                    onChange={handleInputChange}
                    type="number"
                    fullWidth
                  />
                </Grid>
              ))}
            </Grid>
          </GlassPanel>
        </Stack>

        <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md">
          <DialogContent
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 0,
            }}
          >
            <img
              src={currentImage}
              alt="Referencia antropométrica"
              style={{ maxWidth: "100%", maxHeight: "80vh" }}
            />
          </DialogContent>
        </Dialog>
      </Box>
    );
  }
);

export default Antropometria;
